import type { TabType } from '@heidi/session/contracts';

import type { Artifact } from './artifacts/state';
import type { SourceGroundedReference } from './source-grounded-citations';
import type { SourceGroundedSourceKind } from './source-grounded-label';

/**
 * The three session tabs a source reference can land on, RESOLVED BY THE CALLER.
 *
 * This resolver used to read `TabType.DOCUMENT`, `.TRANSCRIPT` and `.CONTEXT`
 * off `@heidi/session/contracts` at six sites, and that VALUE read is what
 * corked the whole source-grounded cluster app-side: a value import of a foreign
 * domain's inert door is an EDGE, not a type contribution —
 * `DOMAIN_TYPE_CONTRIBUTIONS.agents` covers `import type` only and
 * `DOMAIN_EDGES.agents` is `[]` — so this module could not enter a headless
 * package zone at all, and the three modules importing it were held behind it.
 *
 * `import type` STAYS, and that is the point rather than a loophole: `focusTab`
 * is session's vocabulary and has exactly one declaration
 * (`packages/session/src/contracts/tab-type.ts`). The alternative removal path
 * — the three members crossing as an agents-owned union — was refused because it
 * would mint a second declaration of a string enum whose values are wire.
 */
export type SourceGroundedSessionTabs = {
  context: TabType;
  document: TabType;
  transcript: TabType;
};

/**
 * The same record with every member optional, so the navigability predicate can
 * answer with none supplied. No branch below reads a tab value to decide whether
 * a reference resolves — `focusTab` is only ever carried out — and
 * `source-grounded-navigation.test.ts` asserts that rather than trusting it.
 */
type OptionalSessionTabs = Partial<SourceGroundedSessionTabs>;

const SESSION_KINDS: SourceGroundedSourceKind[] = [
  'medications',
  'allergies',
  'problems',
  'lab_result',
  'vital_signs',
  'imaging',
  'ehr_record',
];

type SourceGroundedNavigationRef = Pick<
  SourceGroundedReference,
  | 'kind'
  | 'sessionId'
  | 'documentId'
  | 'patientId'
  | 'memoryId'
  | 'workflowId'
  | 'templateId'
>;

const resolveArtifact = (
  ref: SourceGroundedNavigationRef,
  tabs: OptionalSessionTabs
): Artifact | null => {
  const kind = ref.kind ?? 'unknown';
  const { sessionId, documentId, patientId, memoryId, workflowId, templateId } = ref;

  if (kind === 'library_document' && documentId) {
    return { type: 'library-document', documentId };
  }

  if (kind === 'agent_memory' && memoryId) {
    return { type: 'memory', memoryId };
  }

  if (kind === 'routine' && workflowId) {
    return { type: 'workflow', workflowId };
  }

  if (kind === 'template' && templateId) {
    return { type: 'template-builder', templateId };
  }

  if ((kind === 'patient_profile' || kind === 'patient_summary') && patientId) {
    return { type: 'patient', patientId };
  }

  if (patientId && SESSION_KINDS.includes(kind)) {
    return { type: 'patient', patientId };
  }

  if (sessionId && documentId) {
    if (kind === 'prechart') {
      return {
        type: 'session',
        sessionId,
        focusTab: tabs.document,
        focusDocumentId: documentId,
      };
    }
    if (kind === 'document' || kind === 'attachment') {
      return { type: 'document', sessionId, documentId };
    }
  }

  if (sessionId) {
    switch (kind) {
      case 'consult_note':
        return { type: 'note', sessionId };
      case 'transcript':
        return { type: 'session', sessionId, focusTab: tabs.transcript };
      case 'session_context':
        return { type: 'session', sessionId, focusTab: tabs.context };
      case 'prechart':
        return { type: 'session', sessionId, focusTab: tabs.document };
      case 'open_task':
        return { type: 'tasks', sessionId };
      case 'document':
      case 'attachment':
        return documentId
          ? { type: 'document', sessionId, documentId }
          : { type: 'session', sessionId, focusTab: tabs.document };
      case 'session':
        return { type: 'session', sessionId };
      default:
        if (SESSION_KINDS.includes(kind)) {
          return { type: 'session', sessionId, focusTab: tabs.context };
        }
        return { type: 'session', sessionId };
    }
  }

  return null;
};

/** Map a parsed source reference to a canvas artifact when ids are present. */
export const resolveSourceGroundedArtifact = (
  ref: SourceGroundedNavigationRef,
  tabs: SourceGroundedSessionTabs
): Artifact | null => resolveArtifact(ref, tabs);

export const isSourceGroundedReferenceNavigable = (
  ref: SourceGroundedReference
): boolean => resolveArtifact(ref, {}) != null;
